import { useState, useEffect, useMemo, useCallback } from 'react';
import { Template } from '../../core/template.schemas';

/**
 * Whitelist of fonts suitable for sports content
 */
export const SPORTY_FONT_WHITELIST: string[] = [
  "Anton",
  "Oswald", 
  "Bebas Neue",
  "Teko",
  "Russo One",
  "Rajdhani",
  "Roboto Condensed",
  "Exo 2",
  "Barlow Condensed",
  "League Spartan",
  "Orbitron",
  "Audiowide",
  "Quantico",
  "Changa One",
  "Passion One",
  "Squada One",
  "Montserrat",
  "Poppins",
  "Archivo Black",
  "Kanit"
];

/**
 * Google Fonts API response interface
 */
interface GoogleFont {
  family: string;
  variants: string[];
  subsets: string[];
  version: string;
  lastModified: string;
  files: Record<string, string>;
  category: string;
  kind: string;
  menu: string;
}

interface GoogleFontsResponse {
  kind: string;
  items: GoogleFont[];
}

/**
 * Processed font variant interface
 */
export interface FontVariant {
  weight: string;
  style: 'normal' | 'italic';
  displayName: string;
  value: string;
  googleVariant: string;
}

/**
 * Processed font family interface
 */
export interface FontFamily {
  family: string;
  category: string;
  variants: FontVariant[];
}

/**
 * Hook to fetch and manage Google Fonts
 * Provides utilities for font selection and dynamic loading
 */
export const useGoogleFonts = () => {
  const [fonts, setFonts] = useState<FontFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Process font variants from Google Fonts API
   * Converts variant strings like "regular", "italic", "700", "700italic" to structured data
   */
  const processVariants = useCallback((variants: string[]): FontVariant[] => {
    return variants.map(variant => {
      let weight: string;
      let isItalic = false;

      // Handle different variant formats
      if (variant === 'regular') {
        weight = '400';
        isItalic = false;
      } else if (variant === 'italic') {
        weight = '400';
        isItalic = true;
      } else if (variant.endsWith('italic')) {
        // Extract weight from variants like "700italic"
        weight = variant.replace('italic', '') || '400';
        isItalic = true;
      } else {
        // Pure numeric weight like "700"
        weight = variant;
        isItalic = false;
      }

      // Ensure weight is valid
      const validWeights = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];
      if (!validWeights.includes(weight)) {
        weight = '400'; // Fallback to regular
      }

      const weightDisplayNames: Record<string, string> = {
        '100': 'Thin',
        '200': 'Extra Light',
        '300': 'Light',
        '400': 'Regular',
        '500': 'Medium',
        '600': 'Semi Bold',
        '700': 'Bold',
        '800': 'Extra Bold',
        '900': 'Black'
      };

      const displayName = isItalic 
        ? `${weightDisplayNames[weight] || weight} Italic`
        : weightDisplayNames[weight] || weight;

      return {
        weight,
        style: (isItalic ? 'italic' : 'normal') as 'normal' | 'italic',
        displayName,
        value: variant,
        googleVariant: variant
      };
    }).sort((a, b) => {
      // Sort by weight first, then by style (normal before italic)
      const weightA = parseInt(a.weight);
      const weightB = parseInt(b.weight);
      
      if (weightA !== weightB) {
        return weightA - weightB;
      }
      
      return a.style === 'normal' ? -1 : 1;
    });
  }, []);

  // Fetch fonts from Google Fonts API
  useEffect(() => {
    const fetchFonts = async () => {
      try {
        setLoading(true);
        const apiKey = process.env.REACT_APP_GOOGLE_FONTS_API_KEY;
        
        if (!apiKey) {
          throw new Error('Google Fonts API key not found');
        }

        const response = await fetch(
          `https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch fonts: ${response.statusText}`);
        }

        const data: GoogleFontsResponse = await response.json();
        
        // Filter fonts by whitelist and process variants
        const whitelistedFonts = data.items.filter(font => 
          SPORTY_FONT_WHITELIST.includes(font.family)
        );
        
        const processedFonts = whitelistedFonts.map(font => ({
          family: font.family,
          category: font.category,
          variants: processVariants(font.variants)
        }));

        // Sort fonts by whitelist order (maintain the curated order)
        processedFonts.sort((a, b) => {
          const indexA = SPORTY_FONT_WHITELIST.indexOf(a.family);
          const indexB = SPORTY_FONT_WHITELIST.indexOf(b.family);
          return indexA - indexB;
        });

        setFonts(processedFonts);
        setError(null);
      } catch (err) {
        console.error('Error fetching Google Fonts:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchFonts();
  }, [processVariants]);

  /**
   * Get variants for a specific font family
   */
  const getVariantsForFamily = useCallback((fontFamily: string): FontVariant[] => {
    const font = fonts.find(f => f.family === fontFamily);
    return font?.variants || [];
  }, [fonts]);

  // Module-level cache to track loaded fonts across component remounts
  // This prevents duplicate stylesheet injection when navigating between pages
  const loadedFontUrls = useMemo(() => new Set<string>(), []);

  /**
   * Load a font dynamically by adding it to the document head
   */
  const loadFont = useCallback((fontFamily: string, variants?: string[]) => {
    // Properly encode font family name for URL (spaces -> '+')
    const familyParam = fontFamily.trim().replace(/\s+/g, '+');
  
    // Construct proper Google Fonts v2 URL base
    let fontUrl = `https://fonts.googleapis.com/css2?family=${familyParam}`;
    
    // Add variants if specified
    if (variants && variants.length > 0) {
      const hasItalic = variants.some(v => v.includes('italic'));
  
      if (hasItalic) {
        const allVariants = variants.map(variant => {
          if (variant === 'regular') return '0,400';
          if (variant === 'italic') return '1,400';
          if (variant.endsWith('italic')) {
            const weight = variant.replace('italic', '') || '400';
            return `1,${weight}`;
          }
          return `0,${variant}`;
        });
        
        const uniqueVariants = Array.from(new Set(allVariants));
        uniqueVariants.sort((a, b) => {
          const [styleA, weightA] = a.split(',').map(Number);
          const [styleB, weightB] = b.split(',').map(Number);
          if (styleA !== styleB) {
            return styleA - styleB;
          }
          return weightA - weightB;
        });
  
        fontUrl += `:ital,wght@${uniqueVariants.join(';')}`;
      } else { // only normal weights
        const weights = variants.map(v => v === 'regular' ? '400' : v);
        const uniqueWeights = Array.from(new Set(weights));
        uniqueWeights.sort((a, b) => parseInt(a) - parseInt(b));
        fontUrl += `:wght@${uniqueWeights.join(';')}`;
      }
    }
    
    fontUrl += '&display=swap';

    // CHECK 1: Module-level cache (fastest)
    if (loadedFontUrls.has(fontUrl)) {
      return;
    }

    // CHECK 2: DOM check (fallback for hard reloads or external scripts)
    // Use includes for family param to catch partial matches if exact URL differs slightly
    const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
    if (existingLink) {
      loadedFontUrls.add(fontUrl); // Sync cache
      return; 
    }

    // Double check if we have a very similar link (same family)
    // If so, we might want to skip or merge, but for now just preventing exact dupes is a huge win.
    // The previous check was failing because 'loadedFontUrls' didn't exist, so every remount added a link.
    
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = fontUrl;
    document.head.appendChild(link);
    loadedFontUrls.add(fontUrl);
  }, [loadedFontUrls]);

  /**
   * Preload and make available all fonts used by a template.
   * Injects CSS2 stylesheets for families/variants and waits via CSS Font Loading API.
   */
  const ensureTemplateFontsLoaded = useCallback(async (template: Template, timeoutMs = 5000): Promise<void> => {
    const families = new Map<string, Set<string>>();
  
    // Collect families -> variant tokens (regular | italic | 700 | 700italic)
    template.elements.forEach((el: any) => {
      if (el?.type === 'text') {
        const family = el.fontFamily || 'Inter';
        const weight = (el.fontWeight ?? '400').toString();
        const style = el.fontStyle ?? 'normal';
  
        let token = 'regular';
        if (weight !== '400') token = weight;
        if (style === 'italic') token = weight === '400' ? 'italic' : `${weight}italic`;
  
        if (!families.has(family)) families.set(family, new Set());
        families.get(family)!.add(token);
      }
    });
  
    if (families.size === 0) {
      families.set('Inter', new Set(['regular']));
    }
  
    // Inject stylesheets
    families.forEach((variants: any, family) => {
      loadFont(family, Array.from(variants));
    });
  
    // Wait until fonts are usable
    const fontsApi: any = (document as any).fonts;
    if (!fontsApi?.load) {
      await new Promise((r) => setTimeout(r, 1000));
      return;
    }
  
    const sizePx = 24;
    const descriptors: string[] = [];
    families.forEach((variants, family) => {
      const famQuoted = `"${family}"`;
      Array.from(variants).forEach((variant) => {
        let style = 'normal'; let weight = '400';
        if (variant === 'regular') { style = 'normal'; weight = '400'; }
        else if (variant === 'italic') { style = 'italic'; weight = '400'; }
        else if (variant.endsWith('italic')) { style = 'italic'; weight = variant.replace('italic', '') || '400'; }
        else { style = 'normal'; weight = variant; }
        descriptors.push(`${style} ${weight} ${sizePx}px ${famQuoted}`);
      });
    });
  
    await Promise.all(descriptors.map((d) => fontsApi.load(d)));
    await Promise.race([
      fontsApi.ready,
      new Promise((r) => setTimeout(r, timeoutMs)),
    ]);
  }, [loadFont]);

  /**
   * Get popular/commonly used fonts (all whitelisted fonts are considered popular for sports)
   */
  const popularFonts = useMemo(() => {
    return fonts; // All whitelisted fonts are popular for sports content
  }, [fonts]);

  return {
    fonts,
    popularFonts,
    loading,
    error,
    getVariantsForFamily,
    loadFont,
    ensureTemplateFontsLoaded
  };
};